/* Sipsy: custom engraving (Oct 15 2026, v3: app-agnostic, works with pre-orders).
   Loaded on every page from snippets/cart-drawer.liquid, and on engravable product pages from
   snippets/product-engraving.liquid (the guard below makes the second load a no-op).

   HOW IT ADDS THE ENGRAVING (no button is taken over):
   While the box is checked and the text is valid, the engraving rides along with WHATEVER adds this
   product to the cart: Shopify's Add to Cart, Birdchime, the Rez "Preorder" button, or any future
   pre-order app. Two layers, so it works regardless of how an app builds its request:
     a. hidden inputs  properties[Engraving] / properties[Engraving note] / properties[_engraving_id]
        are attached to the product form (form="..." attribute), so any add that reads the form carries them;
     b. fetch() and XMLHttpRequest calls to /cart/add are watched; if one adds this product's variant
        without our properties (apps that build their own JSON), the properties are added to that request.
   App-specific properties (e.g. a pre-order app's "Preorder note") are kept; ours are only added.
   After the add succeeds, the cart sync (section 2) adds the "Engraving" fee at the same quantity.
   If the box is checked but the text is empty/invalid, clicks on add/pre-order buttons for this
   product are stopped and the shopper is told why (nothing is added without the engraving).

   2. CART SYNC: fee line always equals its bottle's quantity (fee x bottles); bottle removed -> fee removed;
      fee removed on its own -> fee added back. Runs after cart changes made with fetch/XHR and once on load.
   3. SHIPPING ONLY on /cart: hides Birdchime's Local Delivery / Store Pickup tabs (CSS in main-cart-footer
      does it before paint; this is the fallback) and selects Shipping.

   Keep FEE_HANDLE in sync with the Engraving product handle and with the bundle exclusion lists
   (assets/sipsy-bundle.js and bundles/discount-function/src/run.js, title prefix "engraving"). */
(function () {
  'use strict';
  if (window.SipsyEngraving) return;

  var FEE_HANDLE = 'engraving';
  // The order-line note: "Shipping only. <product's Engraving lead-time note, if any> Final sale."
  function noteFor(b) {
    var n = String(b.el.getAttribute('data-note') || '').trim();
    return 'Shipping only. ' + (n ? n + ' ' : '') + 'Final sale.';
  }
  var ALLOWED = /^[A-Za-z0-9 .,'"!?&\-:\/#@()]*$/;
  var CART_ADD = /\/cart\/add(\.js)?(\?|$)/;
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var ADD_LABEL = /add to (cart|bag)|pre-?\s?order|buy|reserve/i;

  var origFetch = window.fetch.bind(window);

  function normalize(s) {
    return String(s || '')
      .replace(/[‘’ʼ]/g, "'")   // iPhone smart apostrophes
      .replace(/[“”]/g, '"')         // smart double quotes
      .replace(/[–—]/g, '-')         // en/em dash
      .replace(/\s+/g, ' ');
  }
  function badChars(s) {
    var out = [];
    Array.from(s).forEach(function (c) {   // Array.from keeps emoji in one piece
      if (!ALLOWED.test(c) && out.indexOf(c) < 0) out.push(c);
    });
    return out;
  }
  function newId() {
    return 'E' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }
  function getCart(fresh) {
    if (typeof window.sipsyFetchCart === 'function') return window.sipsyFetchCart(!!fresh);
    return origFetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }
  function refreshCartUi(open) {
    if (window.SipsyBundle && window.SipsyBundle.refreshCartUi) return window.SipsyBundle.refreshCartUi(open);
    return Promise.all([
      origFetch('/cart?section_id=cart-drawer').then(function (r) { return r.text(); }),
      origFetch('/cart?section_id=cart-icon-bubble').then(function (r) { return r.text(); })
    ]).then(function (res) {
      var parser = new DOMParser();
      var drawer = document.querySelector('cart-drawer');
      var cd = document.getElementById('CartDrawer');
      var nd = parser.parseFromString(res[0], 'text/html').querySelector('#CartDrawer');
      if (cd && nd) cd.innerHTML = nd.innerHTML;
      var nb = parser.parseFromString(res[1], 'text/html').querySelector('#cart-icon-bubble');
      var ob = document.getElementById('cart-icon-bubble');
      if (nb && ob) ob.innerHTML = nb.innerHTML;
      if (drawer) {
        drawer.classList.remove('is-empty');
        var inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        var overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && drawer.close) overlay.addEventListener('click', drawer.close.bind(drawer));
        if (open && drawer.open) drawer.open();
      }
    }).catch(function () {});
  }
  function isCartPage() { return /^\/cart\/?$/.test(location.pathname); }

  // While the fee is being re-synced, cart lines briefly ignore clicks. Shopify's drawer/cart page change
  // lines by POSITION ("line 2"); removing a fee shifts the positions, so a click in that window would
  // hit the wrong line ("line parameter is invalid"). Class on <html> (never re-rendered), opacity only:
  // no layout change. Released when the sync + drawer refresh finish (5s safety timeout).
  var busyTimer;
  function setBusy(on) {
    var root = document.documentElement;
    if (!document.getElementById('SipsyEngraveBusyCss')) {
      var st = document.createElement('style');
      st.id = 'SipsyEngraveBusyCss';
      st.textContent = 'html.sipsy-engrave-busy cart-drawer-items,html.sipsy-engrave-busy cart-items{pointer-events:none;opacity:.6;transition:opacity .15s}';
      document.head.appendChild(st);
    }
    clearTimeout(busyTimer);
    if (on) {
      root.classList.add('sipsy-engrave-busy');
      busyTimer = setTimeout(function () { root.classList.remove('sipsy-engrave-busy'); }, 5000);
    } else {
      root.classList.remove('sipsy-engrave-busy');
    }
  }

  /* ---------------- 1. Product page ---------------- */
  // NOT form.id: Shopify product forms contain <input name="id">, so form.id returns that input.
  function formId(form) { return form ? form.getAttribute('id') : null; }
  var blocks = [];   // { el, form, variants[], eid, hidden{} }

  function setError(b, msg) {
    var err = b.el.querySelector('[data-sipsy-engrave-error]');
    var input = b.el.querySelector('[data-sipsy-engrave-text]');
    if (err) err.textContent = msg || '';
    if (input) input.setAttribute('aria-invalid', msg ? 'true' : 'false');
  }
  // Returns the cleaned text, or null (and shows why) when it can't be used.
  function validate(b, forSubmit) {
    var input = b.el.querySelector('[data-sipsy-engrave-text]');
    var max = parseInt(b.el.getAttribute('data-max'), 10) || 20;
    var raw = normalize(input.value);
    if (raw !== input.value) {
      var pos = input.selectionStart;
      input.value = raw;
      try { input.setSelectionRange(pos, pos); } catch (e) {}
    }
    var text = forSubmit ? raw.trim() : raw;
    var count = b.el.querySelector('[data-sipsy-engrave-count]');
    if (count) count.textContent = text.length + '/' + max;
    var bad = badChars(text);
    if (bad.length) { setError(b, 'Please remove: ' + bad.join(' ') + ' (letters, numbers and basic punctuation only)'); return null; }
    if (text.length > max) { setError(b, 'Please keep it to ' + max + ' characters or fewer.'); return null; }
    if (forSubmit && !text) { setError(b, 'Please enter your engraving text, or uncheck engraving.'); return null; }
    setError(b, '');
    return text;
  }
  function isChecked(b) {
    var on = b.el.querySelector('[data-sipsy-engrave-on]');
    return !!(on && on.checked);
  }
  // Text to attach right now, or null when engraving is off or the text isn't usable (silent check).
  function activeText(b) {
    if (!isChecked(b)) return null;
    var input = b.el.querySelector('[data-sipsy-engrave-text]');
    var max = parseInt(b.el.getAttribute('data-max'), 10) || 20;
    var t = normalize(input.value).trim();
    if (!t || t.length > max || badChars(t).length) return null;
    return t;
  }
  function makeHidden(b, name) {
    var i = document.createElement('input');
    i.type = 'hidden';
    i.name = 'properties[' + name + ']';
    i.setAttribute('form', formId(b.form));
    i.disabled = true;
    b.el.appendChild(i);
    return i;
  }
  // Keep the hidden form inputs in step with the checkbox + text.
  function syncHidden(b) {
    var t = activeText(b);
    var on = t !== null;
    b.hidden.text.value = on ? t : '';
    b.hidden.note.value = on ? noteFor(b) : '';
    b.hidden.eid.value = on ? b.eid : '';
    b.hidden.text.disabled = b.hidden.note.disabled = b.hidden.eid.disabled = !on;
  }
  function initBlock(el) {
    if (el.getAttribute('data-init')) return;
    var form = document.getElementById(el.getAttribute('data-form-id'));
    if (!form) return;
    el.setAttribute('data-init', '1');
    var b = {
      el: el,
      form: form,
      variants: String(el.getAttribute('data-variant-ids') || '').split(',').filter(Boolean),
      eid: newId(),
      hidden: {}
    };
    b.hidden.text = makeHidden(b, 'Engraving');
    b.hidden.note = makeHidden(b, 'Engraving note');
    b.hidden.eid = makeHidden(b, '_engraving_id');
    blocks.push(b);

    var on = el.querySelector('[data-sipsy-engrave-on]');
    var panel = el.querySelector('.sipsy-engrave__panel');
    var input = el.querySelector('[data-sipsy-engrave-text]');
    function sync() {
      var checked = on.checked;
      panel.hidden = !checked;
      on.setAttribute('aria-expanded', checked ? 'true' : 'false');
      if (!checked) setError(b, '');
      syncHidden(b);
    }
    on.addEventListener('change', function () { sync(); if (on.checked) input.focus(); });
    input.addEventListener('input', function () { validate(b, false); syncHidden(b); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); }   // don't submit the product form from here
    });
    sync();   // browsers can restore a checked box on Back
  }
  function initBlocks() {
    var els = document.querySelectorAll('[data-sipsy-engrave]');
    for (var i = 0; i < els.length; i++) initBlock(els[i]);
  }
  function blockForVariant(id) {
    id = String(id);
    for (var i = 0; i < blocks.length; i++) {
      if (blocks[i].variants.indexOf(id) >= 0) return blocks[i];
    }
    return null;
  }

  // Guard: box checked but text missing/invalid -> stop adds of THIS product and say why.
  // An "add control" is any button/submit inside the product form (except +/- quantity buttons),
  // or a button near it whose label reads like add to cart / pre-order / buy.
  function guardTarget(t) {
    if (!t || !t.closest || !blocks.length) return null;
    var ctl = t.closest('button, input[type="submit"], [role="button"]');
    if (!ctl || ctl.closest('[data-sipsy-engrave]') || ctl.closest('quantity-input')) return null;
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      if (!isChecked(b)) continue;
      if (b.form.contains(ctl)) return b;
      var scope = b.form.closest('product-info, section, .product') || b.form.parentNode;
      var label = (ctl.textContent || ctl.value || ctl.getAttribute('aria-label') || '').trim();
      if (scope && scope.contains(ctl) && ADD_LABEL.test(label)) return b;
    }
    return null;
  }
  window.addEventListener('click', function (e) {
    var b = guardTarget(e.target);
    if (!b) return;
    if (validate(b, true) !== null) { syncHidden(b); return; }   // valid: let the normal flow add it
    e.preventDefault();
    e.stopImmediatePropagation();
    var input = b.el.querySelector('[data-sipsy-engrave-text]');
    if (input) input.focus();
  }, true);
  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      if (b.form !== form || !isChecked(b)) continue;
      if (validate(b, true) !== null) { syncHidden(b); return; }
      e.preventDefault();
      e.stopImmediatePropagation();
      var input = b.el.querySelector('[data-sipsy-engrave-text]');
      if (input) input.focus();
      return;
    }
  }, true);

  // ---- request rewriting: make sure an add of this product carries the engraving ----
  function props(b, t) {
    return { 'Engraving': t, 'Engraving note': noteFor(b), '_engraving_id': b.eid };
  }
  // Returns { body, eids[] } with our properties added where needed; body unchanged when nothing applies.
  function rewriteAddBody(body) {
    var used = [];
    function addTo(obj, idKey) {
      var b = blockForVariant(obj[idKey]);
      if (!b) return false;
      var t = activeText(b);
      if (t === null) return false;
      obj.properties = obj.properties || {};
      if (!obj.properties._engraving_id) {
        var p = props(b, t);
        for (var k in p) obj.properties[k] = p[k];
      }
      if (used.indexOf(b) < 0) used.push(b);
      return true;
    }
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var b = blockForVariant(body.get('id'));
        var t = b && activeText(b);
        if (b && t !== null) {
          if (!body.get('properties[_engraving_id]')) {
            var p = props(b, t);
            for (var k in p) body.set('properties[' + k + ']', p[k]);
          }
          used.push(b);
        }
        return { body: body, used: used };
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        var b2 = blockForVariant(body.get('id'));
        var t2 = b2 && activeText(b2);
        if (b2 && t2 !== null) {
          if (!body.get('properties[_engraving_id]')) {
            var p2 = props(b2, t2);
            for (var k2 in p2) body.set('properties[' + k2 + ']', p2[k2]);
          }
          used.push(b2);
        }
        return { body: body, used: used };
      }
      if (typeof body === 'string' && body) {
        var s = body.trim();
        if (s.charAt(0) === '{') {
          var j = JSON.parse(s);
          var changed = false;
          if (Array.isArray(j.items)) j.items.forEach(function (it) { if (it && addTo(it, 'id')) changed = true; });
          else if (j.id && addTo(j, 'id')) changed = true;
          return { body: changed ? JSON.stringify(j) : body, used: used };
        }
        var q = new URLSearchParams(s);
        var b3 = blockForVariant(q.get('id'));
        var t3 = b3 && activeText(b3);
        if (b3 && t3 !== null) {
          if (!q.get('properties[_engraving_id]')) {
            var p3 = props(b3, t3);
            for (var k3 in p3) q.set('properties[' + k3 + ']', p3[k3]);
          }
          used.push(b3);
          return { body: q.toString(), used: used };
        }
      }
    } catch (err) { /* never break an add to cart */ }
    return { body: body, used: used };
  }
  function afterEngravedAdd(used) {
    used.forEach(function (b) { b.eid = newId(); syncHidden(b); });   // next add gets its own line + fee
    window.sipsyEngravingInCart = true;
    setBusy(true);
    setTimeout(syncCart, 300);   // let the app finish its own cart UI, then add the fee and refresh
  }

  /* ---------------- 2. Cart sync ---------------- */
  var syncing = false, again = false, rounds = 0;

  function planFixes(cart) {
    var groups = {}, order = [];
    cart.items.forEach(function (it) {
      var eid = it.properties && it.properties._engraving_id;
      if (!eid) return;
      if (!groups[eid]) { groups[eid] = { bottles: [], fees: [] }; order.push(eid); }
      if (it.handle === FEE_HANDLE) groups[eid].fees.push(it);
      else groups[eid].bottles.push(it);
    });
    var updates = {}, adds = [], any = false;
    order.forEach(function (eid) {
      var g = groups[eid];
      var want = 0;
      g.bottles.forEach(function (b) { want += b.quantity; });
      if (!want) {
        g.fees.forEach(function (f) { updates[f.key] = 0; any = true; });
        return;
      }
      if (!g.fees.length) {
        var b = g.bottles[0];
        adds.push({ eid: eid, qty: want, text: b.properties.Engraving || '', title: b.product_title || b.title });
        any = true;
        return;
      }
      g.fees.forEach(function (f, i) {
        var q = i === 0 ? want : 0;
        if (f.quantity !== q) { updates[f.key] = q; any = true; }
      });
    });
    return { any: any, updates: updates, adds: adds, has: order.length > 0 };
  }

  function feeVariantId() {
    var el = document.querySelector('[data-sipsy-engrave-fee-variant]');
    var b = document.querySelector('[data-sipsy-engrave]');
    return (el && el.getAttribute('data-sipsy-engrave-fee-variant')) || (b && b.getAttribute('data-fee-variant')) || null;
  }

  function syncCart() {
    if (syncing) { again = true; return Promise.resolve(); }
    syncing = true;
    return getCart(true).then(function (cart) {
      var plan = planFixes(cart);
      window.sipsyEngravingInCart = plan.has;
      enforceShippingOnly();
      if (!plan.any) return false;
      if (++rounds > 5) return false;   // safety valve; never loop
      var steps = Promise.resolve();
      if (Object.keys(plan.updates).length) {
        steps = steps.then(function () {
          return origFetch('/cart/update.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: plan.updates })
          });
        });
      }
      var feeId = feeVariantId();
      if (plan.adds.length && feeId) {
        steps = steps.then(function () {
          return origFetch('/cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: plan.adds.map(function (a) {
              return { id: Number(feeId), quantity: a.qty, properties: { 'For': a.title, 'Engraving': a.text, '_engraving_id': a.eid } };
            }) })
          });
        });
      }
      return steps.then(function () {
        if (typeof window.sipsyFetchCart === 'function') window.sipsyFetchCart(true);
        if (isCartPage()) { location.reload(); return true; }
        return refreshCartUi(false).then(function () { return true; });
      });
    }).catch(function () { return false; }).then(function (changed) {
      syncing = false;
      if (again) { again = false; return syncCart(); }
      if (!changed) rounds = 0;
      if (!isCartPage() || !changed) setBusy(false);   // cart page reloads itself after a change
      return changed;
    });
  }

  var syncTimer;
  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncCart, 350);
  }

  // ---- network watch (fetch + XHR) ----
  function urlOf(resource) {
    return typeof resource === 'string' ? resource : (resource && resource.url) || String(resource || '');
  }
  window.fetch = function (resource, init) {
    var url = '', used = [];
    try {
      url = urlOf(resource);
      if (blocks.length && CART_ADD.test(url) && init && init.body != null) {
        var r = rewriteAddBody(init.body);
        used = r.used;
        if (r.body !== init.body) init = Object.assign({}, init, { body: r.body });
      }
    } catch (e) {}
    var p = origFetch(resource, init);
    try {
      if (used.length) {
        p.then(function (res) { if (res && res.ok) afterEngravedAdd(used); }, function () {});
      } else if (CART_WRITE.test(url) && window.sipsyEngravingInCart) {
        p.then(function () { setBusy(true); scheduleSync(); }, function () {});
      }
    } catch (e) {}
    return p;
  };
  if (window.XMLHttpRequest) {
    var xo = XMLHttpRequest.prototype.open, xs = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__sipsyUrl = String(url || ''); } catch (e) {}
      return xo.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      var xhr = this, url = xhr.__sipsyUrl || '', used = [];
      try {
        if (blocks.length && CART_ADD.test(url) && body != null) {
          var r = rewriteAddBody(body);
          used = r.used;
          body = r.body;
        }
        if (used.length || (CART_WRITE.test(url) && window.sipsyEngravingInCart)) {
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              if (used.length) afterEngravedAdd(used); else { setBusy(true); scheduleSync(); }
            }
          });
        }
      } catch (e) {}
      return xs.call(xhr, body);
    };
  }

  /* ---------------- 3. Shipping only (cart page delivery selector) ----------------
     The /cart page has Birdchime's Shipping / Local Delivery / Store Pickup tabs (.bcdp-method-wrap).
     While the cart holds an engraved bottle: hide every tab except Shipping and select Shipping. CSS in
     sections/main-cart-footer.liquid already hides them before first paint; this is the fallback.
     The checkout rule (bundles/engraving-rules-function) enforces the same thing at checkout. */
  var shipClicks = 0;
  function enforceShippingOnly() {
    var container = document.querySelector('.bcdp-methods-container');
    if (!container) return;
    var on = !!window.sipsyEngravingInCart;
    var tabs = container.querySelectorAll('.bcdp-method-wrap');
    var shipTab = null;
    for (var i = 0; i < tabs.length; i++) {
      var txtEl = tabs[i].querySelector('.bcdp-method-wrap-txt');
      var label = (txtEl ? txtEl.textContent : tabs[i].textContent).trim().toLowerCase();
      if (label === 'shipping') { shipTab = tabs[i]; continue; }
      if (on) { tabs[i].style.display = 'none'; tabs[i].setAttribute('aria-hidden', 'true'); tabs[i].setAttribute('data-sipsy-hidden', '1'); }
      else if (tabs[i].getAttribute('data-sipsy-hidden')) { tabs[i].style.display = ''; tabs[i].removeAttribute('aria-hidden'); tabs[i].removeAttribute('data-sipsy-hidden'); }
    }
    if (on && shipTab && !shipTab.classList.contains('bcdp-method-active') && shipClicks < 3) {
      shipClicks++;
      var t = shipTab.querySelector('.bcdp-method-wrap-txt') || shipTab;
      t.click();
    }
  }
  var shipTimer;
  if (isCartPage()) {   // the delivery selector only exists on /cart
    new MutationObserver(function () {
      clearTimeout(shipTimer);
      shipTimer = setTimeout(enforceShippingOnly, 100);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.SipsyEngraving = { sync: syncCart, plan: planFixes, rewrite: rewriteAddBody, shippingOnly: enforceShippingOnly, _blocks: blocks };

  function start() {
    initBlocks();
    // cart-drawer.liquid sets data-sipsy-engrave-in-cart when the page was rendered with engraving lines.
    if (document.querySelector('[data-sipsy-engrave-in-cart]')) {
      window.sipsyEngravingInCart = true;
      scheduleSync();
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
